/**
 * `/campaigns` — every campaign in this workspace.
 *
 * The row says five things, and each one is something a person is about to make
 * a decision with: what it is called and what it sends, which surface it is on,
 * where it has got to, how many people it reaches **on its own channel**, and
 * what has actually happened to them.
 *
 * The reach column is the one to get right. It shows `reach.reachable` — the
 * channel reach — and the segment's people count underneath it as context, never
 * the other way round. A list that advertised "1,278" for an email campaign to a
 * segment where 526 people have an email address would set an expectation the
 * send then misses by a factor of two, and the list is exactly where an
 * expectation about send size is formed.
 *
 * The sent column carries the skipped count beside it, always. "1 sent" on its
 * own is a number that invites a support ticket; "1 sent · 1,277 skipped" is the
 * beginning of an explanation, and the campaign's own screen finishes it.
 *
 * Nothing here is derived on the client. Every figure — the reach, the two
 * counts — is computed by the API on this request from the rows themselves, not
 * read off a stored counter.
 *
 * **Slice 17 touched only the first-run state, and only its length.** The two
 * paragraphs there are the third kind of text in that slice's rule —
 * instruction, for somebody who has never made a campaign — and they are the one
 * place on this screen where that reader exists at all. They kept both facts a
 * person cannot infer from an empty table: that a campaign is a template joined
 * to a segment, one message per person however many lists they are on; and that
 * nothing is sent until it is scheduled. The second is the load-bearing one and
 * is deliberately *not* progressive, because this is the only screen in the
 * product that can put a message in a stranger's inbox and the button on it is
 * primary. What went was the restatement of the channel-reach rule, which the
 * Reachable column beside it makes with this workspace's own numbers.
 *
 * **Slice 44 made the surface the page's dimension.** Every campaign row says
 * which surface it is on — Email or WhatsApp, by the taxonomy's own label —
 * and a surface filter narrows the page to one. Any filter at all — the
 * surface included — makes an empty table "No match", never the first-run
 * screen: a workspace with twelve email campaigns that picks WhatsApp has not
 * made "no campaigns yet".
 *
 * **Slice 65 made this page Connect's alone.** From slice 44 to slice 64 the
 * Meta ad drafts sat here too, in a panel under the table, because the old
 * Reach pillar meant messages and ads at once. Keeping two lists on one
 * screen cost a rule per seam: the integration read before anything else, ad
 * surfaces in the filter for a connected workspace only, a search box cut to
 * the drafts route's shorter limit, an ad surface that hid the table and a
 * status that hid the panel, a second paged read, and a "None yet" chip so
 * "No campaigns yet" was not printed above a panel of ads. The split gave
 * the ads to Reach, at `/reach/ads`, and every one of those rules went with
 * them. This page now calls no Meta ads route at all — not even the
 * integration read, which belongs to the page that needs it — and its surface
 * filter offers the message surfaces only, the ones a campaign here can be on.
 * The search box kept a refusal, at the campaigns list's own limit
 * (`CAMPAIGN_SEARCH_MAX`) rather than the drafts route's shorter one: without
 * it a long paste is sent, refused with a 400, and drawn as the page failing
 * to load.
 * The rejected alternative, a link to the ads left on this page "for people
 * used to finding them here", would have been a Reach destination inside
 * Connect; the sidebar's Reach group and the old addresses' redirects are how
 * they are found.
 *
 * The page header names the pillar — Connect — and says in one line what it
 * is for, its eyebrow from `lib/pillars.tsx` like every pillar's screens. The
 * line says who the campaigns go to and through whose accounts; it does not
 * promise "a receipt for every person", which a review caught: in this app a
 * receipt is a WhatsApp read receipt, which many recipients switch off and
 * email does not have, and the results screen under this list says exactly
 * that. "New campaign"
 * stays the one primary action, in the top bar where every screen keeps its
 * actions.
 */
import {
  CAMPAIGN_SEARCH_MAX,
  CONNECT_CAMPAIGN_STATUS_VALUES,
  DEFAULT_PAGE_SIZE,
  SURFACE_CAPABILITY,
  SURFACE_ORDER,
  campaignSortSchema,
  campaignStatusSchema,
  surfaceIsBuilt,
  surfaceSchema,
  type CampaignStatus,
  type CampaignSummary,
  type Surface,
} from '@wend/shared';
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  Input,
  Meter,
  PageHeader,
  Select,
  Spinner,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
  type SelectOption,
} from '@wend/ui';
import { Plus, Search, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { CampaignReachCell } from '../../components/CampaignReach';
import { CampaignStatusBadge } from '../../components/CampaignStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { listCampaigns } from '../../lib/api/campaigns';
import { errorMessage } from '../../lib/api/errors';
import { CAMPAIGN_STATUS_DISPLAY, formatInTimeZone, sentShare } from '../../lib/campaign-display';
import { formatCount, formatDateTime, pluralise } from '../../lib/format';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { channelSurfaceLabel, surfaceLabel } from '../../lib/surface-display';
import { useAsyncData } from '../../lib/use-async-data';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

/** Where the search box's own refusal is announced from. */
const SEARCH_REFUSAL_ID = 'campaigns-search-refusal';

/**
 * Radix reserves `''` for "show the placeholder", so "every status" needs a
 * sentinel of its own rather than an empty value that can never be selected.
 */
const ANY_STATUS = '__any__';

const STATUS_OPTIONS: readonly SelectOption[] = [
  { value: ANY_STATUS, label: 'Any status' },
  // Slice 68: `flow` is a flow step's status and never a Connect campaign's.
  ...CONNECT_CAMPAIGN_STATUS_VALUES.map((status) => ({
    value: status,
    label: CAMPAIGN_STATUS_DISPLAY[status].label,
    description: CAMPAIGN_STATUS_DISPLAY[status].meaning,
  })),
];

/** "Every surface" — a sentinel for the same reason as {@link ANY_STATUS}. */
const ANY_SURFACE = '__any__';

/** Whether a surface is a message's — one a campaign on this page can be on. */
function isMessageSurface(surface: Surface): boolean {
  return SURFACE_CAPABILITY[surface].kind === 'direct';
}

/**
 * The surfaces a campaign on this page can be on, in taxonomy order: the built
 * message surfaces, Email and WhatsApp — SMS joins when its adapter exists and
 * `surfaceIsBuilt` says so. No ad surface since slice 65: the ads are Reach's,
 * at `/reach/ads`, and an option here that could only ever say "No match"
 * would be a trap.
 */
const SURFACE_OPTIONS: readonly SelectOption[] = [
  { value: ANY_SURFACE, label: 'Any surface' },
  ...SURFACE_ORDER.filter(surfaceIsBuilt)
    .filter(isMessageSurface)
    .map((surface) => ({ value: surface, label: surfaceLabel(surface) })),
];

export function CampaignsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<CampaignStatus | null>(null);
  const [surface, setSurface] = useState<Surface | null>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'createdAt', direction: 'desc' });

  /*
    The campaigns list takes a search of `CAMPAIGN_SEARCH_MAX` characters.
    Longer is refused on the box rather than sent: the list would answer 400,
    and this page can only draw that as "could not load your campaigns" — an
    outage, for a search that was merely too long. Not truncated either, which
    would make "No match" a statement about a search nobody typed. Slice 52's
    rule, which the Reach page keeps at the drafts route's own limit.
  */
  const tooLong = searchInput.trim().length > CAMPAIGN_SEARCH_MAX;

  useEffect(() => {
    if (tooLong) return;
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput, tooLong]);

  const sortField = campaignSortSchema.catch('createdAt').parse(sort.key);

  const channel = surface === null ? null : SURFACE_CAPABILITY[surface].channel;

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    async (signal) =>
      listCampaigns(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          sort: sortField,
          direction: sort.direction,
          ...(search === '' ? {} : { search }),
          ...(status === null ? {} : { status }),
          ...(channel === null ? {} : { channel }),
        },
        signal,
      ),
    [page, search, status, channel, sortField, sort.direction],
  );

  const columns = useMemo<Array<DataTableColumn<CampaignSummary>>>(
    () => [
      {
        key: 'name',
        header: 'Campaign',
        sortable: true,
        cell: (campaign) => (
          <div className="min-w-0">
            <Link
              to={`/campaigns/${campaign.id}`}
              className="font-medium text-text underline-offset-4 hover:underline"
            >
              {campaign.name}
            </Link>
            <p className="truncate text-[0.82rem] text-muted">
              {campaign.templateName} <span className="text-muted">to</span> {campaign.segmentName}
            </p>
          </div>
        ),
      },
      {
        /* Slice 44: the surface, by the taxonomy's label. Narrow: the table
           still fits a 1440px screen, and below that it scrolls sideways
           inside its own card, as it already did at 1280. */
        key: 'surface',
        header: 'Surface',
        width: '6.5rem',
        cell: (campaign) => <Badge tone="neutral">{channelSurfaceLabel(campaign.channel)}</Badge>,
      },
      {
        key: 'status',
        header: 'Status',
        width: '9rem',
        cell: (campaign) => <CampaignStatusBadge status={campaign.status} />,
      },
      {
        key: 'reach',
        // "Reachable", not "Reach": since slice 65 the capitalised word is the
        // paid-ads pillar, a heading in the sidebar above this page. The figure
        // is how many of the segment this campaign's channel can reach.
        header: 'Reachable',
        width: '11rem',
        cell: (campaign) => <CampaignReachCell reach={campaign.reach} />,
      },
      {
        key: 'sent',
        header: 'Sent',
        align: 'right',
        width: '9rem',
        // Both halves or neither. A sent count with the skipped count hidden one
        // click away is the shape of a number somebody misreads as the whole
        // outcome — and in a guarded environment it is the smaller half.
        //
        // Slice 66: the counts are sans with tabular digits (mono's dotted zero
        // reads as an 8 in a column of small numbers), and a finished campaign
        // gets a thin brand meter under them: sent out of its own resolved
        // recipients, the two numbers printed beside it. NOT sent against the
        // Reachable column — that is the segment's reach today, not this
        // campaign's list, and the first build's bar made an allow-listed
        // send look like a failed one. `sentShare` says why and when it is
        // drawn. It is magnitude only, never a verdict colour; the counts stay
        // the claim and the meter is `aria-hidden` decoration on them.
        cell: (campaign) =>
          campaign.status === 'draft' ? (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
              not built
            </span>
          ) : (
            <div className="flex flex-col items-end gap-1 tabular-nums">
              <p className="text-[0.85rem] font-medium text-text">{formatCount(campaign.sentCount)} sent</p>
              {(() => {
                const share = sentShare(campaign);
                return share === null ? null : <Meter value={share} tone="brand" className="w-[72px]" />;
              })()}
              <p className="text-[0.72rem] text-muted">
                {formatCount(campaign.skippedCount)} skipped
              </p>
            </div>
          ),
      },
      {
        key: 'scheduledAt',
        header: 'Scheduled',
        sortable: true,
        width: '13rem',
        cell: (campaign) =>
          campaign.scheduledAt === null ? (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
              no time yet
            </span>
          ) : (
            // In the campaign's own timezone, which is why the zone is stored
            // alongside the instant: "did they mean 9am in Mumbai?" is the
            // question, and the reader's own clock answers a different one.
            <span className="whitespace-nowrap text-muted">
              {formatInTimeZone(campaign.scheduledAt, campaign.timezone)}
            </span>
          ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        sortable: true,
        width: '11rem',
        cell: (campaign) => (
          <span className="whitespace-nowrap text-muted">{formatDateTime(campaign.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;
  const filtered = search !== '' || status !== null || surface !== null;

  return (
    <AppLayout
      title="Campaigns"
      actions={
        <Link to="/campaigns/new" className={buttonVariants({ size: 'sm' })}>
          <Plus aria-hidden="true" className="size-4" />
          New campaign
        </Link>
      }
    >
      <PageHeader
        eyebrow={PILLAR_IDENTITY.connect.label}
        icon={PILLAR_IDENTITY.connect.icon}
        lede="Message the people you already hold, by email and WhatsApp, from your own accounts."
      />

      <div className="flex flex-col gap-5">
        {failedToLoad ? (
          <Card>
            <ErrorState error={error} title="Could not load your campaigns" onRetry={reload} />
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1 sm:max-w-xs">
                <div className="relative">
                  <Search
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                  />
                  <Input
                    type="search"
                    className="pl-9"
                    placeholder="Search by campaign name"
                    aria-label="Search campaigns"
                    value={searchInput}
                    aria-invalid={tooLong || undefined}
                    aria-describedby={tooLong ? SEARCH_REFUSAL_ID : undefined}
                    onChange={(event) => {
                      setSearchInput(event.target.value);
                    }}
                  />
                </div>
                {tooLong && (
                  <p id={SEARCH_REFUSAL_ID} data-role="campaigns-search-too-long" className="mt-1.5 text-[0.82rem] text-bad">
                    Search for {CAMPAIGN_SEARCH_MAX} characters or fewer.
                  </p>
                )}
              </div>

              <Select
                size="sm"
                className="w-auto min-w-[10rem]"
                aria-label="Filter by surface"
                options={SURFACE_OPTIONS}
                value={surface ?? ANY_SURFACE}
                onValueChange={(value) => {
                  // As the status sentinel: a failed parse is "any surface", and
                  // so is a surface no campaign here can be on.
                  const parsed = surfaceSchema.safeParse(value);
                  setSurface(parsed.success && isMessageSurface(parsed.data) ? parsed.data : null);
                  setPage(1);
                }}
              />

              <Select
                size="sm"
                className="w-auto min-w-[11rem]"
                aria-label="Filter by status"
                options={STATUS_OPTIONS}
                value={status ?? ANY_STATUS}
                onValueChange={(value) => {
                  // The sentinel is not a member of the enum, so a failed parse
                  // is exactly "any status" rather than something to report.
                  const parsed = campaignStatusSchema.safeParse(value);
                  setStatus(parsed.success ? parsed.data : null);
                  setPage(1);
                }}
              />

              {refreshing && <Spinner size="sm" label="Updating the campaigns" />}

              {pagination !== null && pagination.total > 0 && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {pluralise(pagination.total, 'campaign')}
                </p>
              )}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The campaigns below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Campaigns"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(campaign) => campaign.id}
              loading={loadingFirst}
              sort={sort}
              onSortChange={setSort}
              minWidth="60rem"
              empty={filtered ? <NoMatch /> : <FirstCampaign />}
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty states                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The first-run state.
 *
 * It has to say two things a person cannot infer from an empty table: that a
 * campaign is the joining of a template to a segment at a time, and that nothing
 * is sent until it is scheduled. The second matters more than it looks — this is
 * the only screen in the product that can put a message in a stranger's inbox,
 * and knowing where the point of no return is should not require finding it.
 */
function FirstCampaign() {
  return (
    <CardContent className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <span className="grid size-12 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
        <Send aria-hidden="true" className="size-5" />
      </span>

      <div className="flex max-w-[38rem] flex-col gap-2.5">
        <h2 className="text-[1.1rem] font-semibold tracking-[-0.022em]">No campaigns yet</h2>
        <p className="text-[0.92rem] leading-relaxed text-muted">
          A campaign sends one approved template to one segment, at a time you choose — one message
          per person, however many of your lists they are on.
        </p>
        {/* Kept, in one line. Somebody who believes this button sends something
            will not press it, and somebody who believes it *might* will press it
            once and find out. Both are worse than a sentence. */}
        <p className="text-[0.92rem] leading-relaxed text-muted">
          Creating one sends nothing. Scheduling builds the recipient list, and that screen says how
          many people will actually receive it before you commit.
        </p>
      </div>

      <Link to="/campaigns/new" className={buttonVariants({ size: 'sm' })}>
        <Plus aria-hidden="true" className="size-4" />
        Create your first campaign
      </Link>
    </CardContent>
  );
}

/**
 * The filters matched no campaign. A chip, as the Meta ads panel's own "No
 * match" — slice 44 made it one because two sentences here sat directly above
 * a panel listing the ad campaign the same search did match, and read as a
 * contradiction. The panel left in slice 65; the chip stays, because one
 * empty-result word across the product's lists is worth more than the two
 * sentences were.
 */
function NoMatch() {
  return (
    <div className="flex justify-center py-4">
      <Badge tone="neutral" data-role="campaigns-no-match">
        No match
      </Badge>
    </div>
  );
}
